<script setup lang="ts">
import type { ConfigField, ConfigFieldGroup } from "../types";
import VModernSelect from "@verbleif/lib/src/components/AutoComplete/VModernSelect.vue";
import VInput from "@verbleif/lib/src/components/Input/VInput.vue";
import VListCard from "@verbleif/lib/src/components/ListCard/VListCard.vue";
import VRadioTile from "@verbleif/lib/src/components/RadioTile/VRadioTile.vue";
import VRadioTileGroup from "@verbleif/lib/src/components/RadioTile/VRadioTileGroup.vue";
import VSectionHeading from "@verbleif/lib/src/components/SectionHeading/VSectionHeading.vue";
import VTag from "@verbleif/lib/src/components/Tag/VTag.vue";
import { computed, inject, nextTick, useId } from "vue";
import { useI18n } from "vue-i18n";
import { APP_ENVIRONMENTS } from "../configPresets";
import DebugCard from "../parts/DebugCard.vue";
import DebugRow from "../parts/DebugRow.vue";
import { DEBUG_TOOLS_CONTEXT } from "../types";
import { useDebugEnvironment } from "../useDebugEnvironment";
import { CUSTOM_PRESET } from "./environmentForm";
import { useEnvironmentForm } from "./useEnvironmentForm";

const context = inject(DEBUG_TOOLS_CONTEXT, null);
if (context === null) {
  throw new Error("The Omgeving section only works inside the debug tools dialog.");
}

const { t } = useI18n();
const form = useEnvironmentForm();
const idPrefix = useId();
const { presetKeys, presetSubtitle } = useDebugEnvironment(context.app, context.config);

const debugMode = computed({
  get: () => context.config.debugMode.value,
  set: (value: boolean) => {
    context.config.debugMode.value = value;
  },
});

const customSubtitle = computed(() => {
  if (form.selection.value === CUSTOM_PRESET && form.basePreset.value !== null) {
    return t("debug_tools.environment.custom_based_on", { preset: form.presetLabel(form.basePreset.value) });
  }
  return t("debug_tools.environment.custom_own");
});

const appEnvironmentItems = APP_ENVIRONMENTS.map(value => ({ name: value, value }));

interface FieldGroup {
  key: ConfigFieldGroup | "all"
  fields: ConfigField[]
}

const groups = computed<FieldGroup[]>(() => {
  if (form.fields.every(field => field.group === undefined)) {
    return [{ key: "all", fields: form.fields }];
  }
  const order: ConfigFieldGroup[] = ["connections", "login"];
  return order
    .map(key => ({ key, fields: form.fields.filter(field => field.group === key) }))
    .filter(group => group.fields.length > 0);
});

function fieldId(field: ConfigField): string {
  return `${idPrefix}-${field.key}`;
}

async function onSelect(value: string | number | boolean | null) {
  if (value === null || typeof value !== "string") {
    return;
  }
  if (value === CUSTOM_PRESET) {
    await nextTick();
    const first = form.fields[0];
    if (first) {
      document.getElementById(fieldId(first))?.focus();
    }
    return;
  }
  await form.applyPreset(value);
}
</script>

<template>
  <div class="flex flex-col gap-4" data-testid="debug-environment">
    <VSectionHeading
      :level="3"
      :title="t('debug_tools.environment.title')"
      :help="t('debug_tools.environment.help')"
    />

    <VListCard as="dl" :aria-label="t('debug_tools.environment.debug_mode')">
      <DebugRow
        v-model="debugMode"
        :label="t('debug_tools.environment.debug_mode')"
        :help="t('debug_tools.environment.debug_mode_help')"
        is-switch
        data-testid="debug-environment-debug-mode"
      />
    </VListCard>

    <DebugCard as="div" :title="t('debug_tools.environment.preset')">
      <div class="p-3.5">
        <VRadioTileGroup
          :model-value="form.selection.value"
          :aria-label="t('debug_tools.environment.preset')"
          layout="grid"
          :columns="4"
          variant="stacked"
          :disabled="form.busy.value"
          @update:model-value="onSelect"
        >
          <VRadioTile
            v-for="key in presetKeys"
            :key="key"
            :value="key"
            :title="form.presetLabel(key)"
            :subtitle="presetSubtitle(key)"
            :test-id="`debug-environment-preset-${key}`"
          />
          <VRadioTile
            :value="CUSTOM_PRESET"
            :title="t('debug_tools.environment.custom')"
            :subtitle="customSubtitle"
            test-id="debug-environment-preset-custom"
          />
        </VRadioTileGroup>
      </div>
    </DebugCard>

    <DebugCard as="div" :title="t('debug_tools.environment.addresses')">
      <div class="flex flex-col gap-2.5 p-3.5">
        <template v-for="group in groups" :key="group.key">
          <div v-if="group.key !== 'all'" class="mt-1 text-xs font-medium text-gray-600 dark:text-gray-400">
            {{ t(`debug_tools.environment.groups.${group.key}`) }}
          </div>
          <div class="grid grid-cols-1 gap-x-4 gap-y-3 md:grid-cols-2">
            <div v-for="field in group.fields" :key="field.key" class="relative" :data-testid="`debug-environment-field-${field.key}`">
              <VTag
                v-if="form.changedKeys.value.includes(field.key)"
                class="absolute top-0 right-0"
                :text="t('debug_tools.environment.changed')"
                variant="is-info"
                size="extra-small"
                icon-less
                data-testid="debug-environment-changed"
              />
              <VModernSelect
                v-if="field.type === 'env'"
                :id="fieldId(field)"
                v-model="form.values[field.key]"
                :items="appEnvironmentItems"
                key-field-name="value"
                :label-left="t(field.label)"
                :placeholder="t(field.placeholder)"
                :error-message="form.errors.value[field.key]"
                show-error-below
                hide-clear
                required
              />
              <VInput
                v-else
                :id="fieldId(field)"
                v-model="form.values[field.key]"
                :type="field.type === 'url' ? 'url' : 'text'"
                :label-left="t(field.label)"
                :placeholder="t(field.placeholder)"
                :error-message="form.errors.value[field.key]"
                show-error-below
                required
              />
            </div>
          </div>
        </template>
      </div>
    </DebugCard>
  </div>
</template>
